// frontend/src/pages/ReportsPage.js
import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container, Box, Typography, Paper, Grid, Card,
  CardContent, CardActionArea, Chip,
} from '@mui/material';
import {
  TrendingUp as DailyIcon,
  Warning as ComplaintIcon,
  EmojiEvents as OfficerIcon,
  SwapHoriz as TransferIcon,
  HowToReg as PreClearIcon,
  ArrowBack as BackIcon,
} from '@mui/icons-material';
import nisLogoWithText from '../assets/images/immgrationlogo1.png';

const REPORTS = [
  {
    id:          'personnel-verification-status',
    title:       'Complete Personnel Verification Status',
    description: 'Comprehensive detailed list of ALL personnel with verification status, tax state, contact info, and non-verification reasons — ready for management reporting.',
    icon:        <PreClearIcon sx={{ fontSize: 40 }} />,
    color:       '#1a237e',
    bg:          '#e8eaf6',
    path:        '/reports/personnel-verification-status',
    chip:        'COMPREHENSIVE',
    chipColor:   'secondary',
  },
  {
    id:          'daily-progress',
    title:       'Daily Progress Report',
    description: 'Day-by-day verification counts with top zones and officers for any date range.',
    icon:        <DailyIcon sx={{ fontSize: 40 }} />,
    color:       '#6a1b9a',
    bg:          '#f3e5f5',
    path:        '/reports/daily-progress',
    chip:        'Trend',
    chipColor:   'secondary',
  },
  {
    id:          'salary-complaints',
    title:       'Salary Complaints Report',
    description: 'All salary complaints by type and zone with personnel and verification details.',
    icon:        <ComplaintIcon sx={{ fontSize: 40 }} />,
    color:       '#c62828',
    bg:          '#ffebee',
    path:        '/reports/salary-complaints',
    chip:        'Complaints',
    chipColor:   'error',
  },
  {
    id:          'officer-performance',
    title:       'Officer Performance Report',
    description: 'Total verifications per officer with daily averages, active days and peak performance.',
    icon:        <OfficerIcon sx={{ fontSize: 40 }} />,
    color:       '#00695c',
    bg:          '#e0f2f1',
    path:        '/reports/officer-performance',
    chip:        'Performance',
    chipColor:   'success',
  },
  {
    id:          'transfer-requests',
    title:       'Transfer Requests Report',
    description: 'All personnel transfer requests by officers — who requested, from/to command, reason, and status.',
    icon:        <TransferIcon sx={{ fontSize: 40 }} />,
    color:       '#f57f17',
    bg:          '#fffde7',
    path:        '/reports/transfer-requests',
    chip:        'Transfers',
    chipColor:   'warning',
  },
];

const ReportsPage = () => {
  const navigate = useNavigate();

  return (
    <Container maxWidth="xl">
      <Box sx={{ mt: 4, mb: 6 }}>

        {/* Header */}
        <Paper elevation={2} sx={{ p: 3, mb: 4 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box display="flex" alignItems="center" gap={2.5}>
              <Box
                component="img"
                src={nisLogoWithText}
                alt="Nigeria Immigration Service"
                sx={{ height: 50, width: 'auto', objectFit: 'contain' }}
              />
              <Box>
                <Typography variant="h6" fontWeight="600"
                  sx={{ color: '#00a651', lineHeight: 1.2, mb: 0.5, textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '1.1rem' }}>
                  Reports
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>
                  NIS Emolument Verification Exercise — Select a report to generate and export
                </Typography>
              </Box>
            </Box>
            <Box
              onClick={() => navigate('/')}
              sx={{
                display: 'flex', alignItems: 'center', gap: 1,
                cursor: 'pointer', color: 'primary.main',
                '&:hover': { textDecoration: 'underline' },
              }}
            >
              <BackIcon fontSize="small" />
              <Typography variant="body2">Back to Dashboard</Typography>
            </Box>
          </Box>
        </Paper>

        {/* Report Cards Grid */}
        <Grid container spacing={3}>
          {REPORTS.map((report) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={report.id}>
              <Card
                elevation={2}
                sx={{
                  height: '100%',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': { transform: 'translateY(-4px)', boxShadow: 6 },
                  border: '1px solid',
                  borderColor: 'grey.200',
                }}
              >
                <CardActionArea
                  onClick={() => navigate(report.path)}
                  sx={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'flex-start', p: 0 }}
                >
                  <CardContent sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>

                    {/* Icon */}
                    <Box
                      sx={{
                        width: 72, height: 72, borderRadius: 3,
                        bgcolor: report.bg, color: report.color,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        mb: 2,
                      }}
                    >
                      {report.icon}
                    </Box>

                    {/* Chip */}
                    <Chip
                      label={report.chip}
                      color={report.chipColor}
                      size="small"
                      variant="outlined"
                      sx={{ mb: 1.5, alignSelf: 'flex-start', fontWeight: 'bold', fontSize: '0.7rem' }}
                    />

                    {/* Title */}
                    <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ lineHeight: 1.3 }}>
                      {report.title}
                    </Typography>

                    {/* Description */}
                    <Typography variant="body2" color="text.secondary" sx={{ flex: 1, lineHeight: 1.5 }}>
                      {report.description}
                    </Typography>

                    {/* Click hint */}
                    <Typography
                      variant="caption"
                      sx={{ mt: 2, color: report.color, fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 0.5 }}
                    >
                      Generate &amp; Export → Excel
                    </Typography>
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          ))}
        </Grid>

      </Box>
    </Container>
  );
};

export default ReportsPage;